/**
 * Same behavior as @backstage/core-components OAuthRequestDialog, but formats
 * GitLab popup-closed errors with an explicit "refresh this page" hint (Incognito).
 */
import { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import useObservable from 'react-use/esm/useObservable';
import { isError } from '@backstage/errors';
import {
  configApiRef,
  oauthRequestApiRef,
  useApi,
  type PendingOAuthRequest,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { coreComponentsTranslationRef } from '@backstage/core-components/alpha';
import { formatAuthFailureUserMessage } from '../../utils/oauthLoginErrorHints';

const useDialogStyles = makeStyles(
  theme => ({
    dialog: {
      paddingTop: theme.spacing(1),
    },
    title: {
      minWidth: 0,
    },
    titleHeading: {
      fontSize: theme.typography.h6.fontSize,
    },
    contentList: {
      padding: 0,
    },
    actionButtons: {
      padding: theme.spacing(2, 0),
    },
  }),
  { name: 'IronPhoenixOAuthRequestDialog' },
);

const useItemStyles = makeStyles(
  theme => ({
    root: {
      paddingLeft: theme.spacing(2),
    },
    button: {
      marginLeft: theme.spacing(2),
    },
  }),
  { name: 'IronPhoenixLoginRequestListItem' },
);

function IronPhoenixLoginRequestListItem({
  request,
  busy,
  setBusy,
}: {
  request: PendingOAuthRequest;
  busy: boolean;
  setBusy: (v: boolean) => void;
}) {
  const classes = useItemStyles();
  const [error, setError] = useState<string>();
  const { t } = useTranslationRef(coreComponentsTranslationRef);
  const configApi = useApi(configApiRef);

  const handleContinue = async () => {
    setBusy(true);
    try {
      await request.trigger();
    } catch (e) {
      const raw = isError(e) ? e.message : 'An unspecified error occurred';
      const errorName = isError(e) ? e.name : undefined;
      setError(formatAuthFailureUserMessage(raw, errorName));
    } finally {
      setBusy(false);
    }
  };

  const IconComponent = request.provider.icon;
  const message =
    request.provider.message ??
    t('oauthRequestDialog.message', {
      appTitle: configApi.getString('app.title'),
      provider: request.provider.title,
    });

  return (
    <ListItem
      disabled={busy}
      classes={{ root: classes.root }}
      alignItems="flex-start"
    >
      <ListItemAvatar>
        <IconComponent fontSize="large" />
      </ListItemAvatar>
      <Box flex={1} minWidth={0} paddingRight={1}>
        <Typography variant="subtitle1" component="div" gutterBottom>
          {request.provider.title}
        </Typography>
        {message ? (
          <Typography
            variant="body2"
            color="textSecondary"
            component="div"
            style={{ marginBottom: error ? 12 : 0 }}
          >
            {message}
          </Typography>
        ) : null}
        {error ? (
          <Typography
            variant="body2"
            color="error"
            component="div"
            style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}
          >
            {error}
          </Typography>
        ) : null}
      </Box>
      <Box flexShrink={0} alignSelf="center">
        <Button
          color="primary"
          variant="contained"
          onClick={handleContinue}
          className={classes.button}
        >
          {t('oauthRequestDialog.login')}
        </Button>
      </Box>
    </ListItem>
  );
}

export function IronPhoenixOAuthRequestDialog() {
  const classes = useDialogStyles();
  const [busy, setBusy] = useState(false);
  const oauthRequestApi = useApi(oauthRequestApiRef);
  const configApi = useApi(configApiRef);
  const { t } = useTranslationRef(coreComponentsTranslationRef);
  const authRedirect =
    configApi.getOptionalBoolean('enableExperimentalRedirectFlow') ?? false;

  const requests = useObservable(
    useMemo(() => oauthRequestApi.authRequest$(), [oauthRequestApi]),
    [],
  );

  const handleRejectAll = () => {
    requests.forEach(r => r.reject());
  };

  return (
    <Dialog
      open={Boolean(requests.length)}
      fullWidth
      maxWidth="sm"
      classes={{ paper: classes.dialog }}
      aria-labelledby="oauth-req-dialog-title"
    >
      <main>
        <DialogTitle
          classes={{ root: classes.title }}
          id="oauth-req-dialog-title"
        >
          <Typography
            className={classes.titleHeading}
            variant="h1"
            component="span"
          >
            {t('oauthRequestDialog.title')}
          </Typography>
          {authRedirect ? (
            <Typography>{t('oauthRequestDialog.authRedirectTitle')}</Typography>
          ) : null}
        </DialogTitle>
        <DialogContent dividers classes={{ root: classes.contentList }}>
          <List>
            {requests.map(request => (
              <IronPhoenixLoginRequestListItem
                key={request.provider.title}
                request={request}
                busy={busy}
                setBusy={setBusy}
              />
            ))}
          </List>
        </DialogContent>
      </main>
      <DialogActions classes={{ root: classes.actionButtons }}>
        <Button onClick={handleRejectAll}>
          {t('oauthRequestDialog.rejectAll')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
